/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './WaveDots';

const WaveDotsManager: ComponentManagerFn<'WaveDots'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'WaveDots'>(platform, 'WaveDots'),
    type: 'WaveDots',
    name: 'Wave Dots',
    defaultValue,
    props: {},
    category: 'Dashboard',
    icon: { iconName: 'ellipsis-h' },
    description: `**Overview**<br/>
Wave Dots is a minimalist visual component used to indicate active processing or loading states. Unlike a heavy modal spinner that blocks the entire screen, this component displays a subtle, animated sequence of dots.
It is designed to reassure the user that a background task—such as fetching data, calculating metrics, or initializing a dashboard widget—is currently in progress, without interrupting their workflow.

**1. Configuration**<br/>
The Wave Dots component is designed for "Zero Configuration" usage. It has no properties to configure because its sole purpose is to provide a standardized, consistent loading signal across your entire application.
* **Behavior:** Simply drag and drop the component onto the canvas. It will begin animating immediately upon rendering and continue until it is removed or hidden.

**2. Common Use Cases**<br/>
* **Inline Loading:** Place it inside a Button or next to a Text Field to show that a specific action (like "Saving..." or "Validating...") is happening.
* **Data Placeholders:** Use it inside a Card or Chart container while the actual data is being fetched from the server. This prevents the user from seeing a blank or broken UI before the content loads.
* **Conditional Feedback:** Combine it with visibility logic (e.g., "Show this component only when \`$.myDataStore.isLoading\` is true") to create dynamic loading states.

**3. Layout & Sizing**<br/>
* **Adaptive:** The component automatically adapts to the layout constraints of its parent container. It is lightweight and does not force fixed dimensions, making it easy to slip into headers, footers, or dense data grids.`,
  };
};

export default WaveDotsManager;
